import { BASIS_POINTS_FULL } from '@/domain/money'

// Taken from the domain rather than spelled again, because the page writes the
// key the valuation reads: two spellings would leave the switches saved and
// every figure computed as though nobody had touched them.
export { PRIVATE_HOLDINGS_SETTING_KEY } from '@/domain/private-holding'

export type PrivateHoldingsMeasure =
  | 'lookThrough'
  | 'spendable'
  | 'liquid'
  | 'runway'
  | 'concentration'

export interface PrivateHoldingsSettings {
  includeInLookThrough: boolean
  includeInConcentration: boolean
  haircutBp: number
}

/**
 * Owning the shares is a fact, so they belong in the headline. Nothing else is:
 * they cannot be sold to cover a month of spending, and left in a concentration
 * figure they answer "what is your largest position" with the same name forever.
 */
export const PRIVATE_HOLDINGS_DEFAULTS: PrivateHoldingsSettings = {
  includeInLookThrough: true,
  includeInConcentration: false,
  haircutBp: 0,
}

export function parsePrivateHoldingsSettings(value: unknown): PrivateHoldingsSettings {
  const stored = isRecord(value) ? value : {}
  return {
    includeInLookThrough: asBoolean(
      stored.includeInLookThrough,
      PRIVATE_HOLDINGS_DEFAULTS.includeInLookThrough,
    ),
    includeInConcentration: asBoolean(
      stored.includeInConcentration,
      PRIVATE_HOLDINGS_DEFAULTS.includeInConcentration,
    ),
    haircutBp: asHaircutBp(stored.haircutBp, PRIVATE_HOLDINGS_DEFAULTS.haircutBp),
  }
}

/**
 * The stored blob is shared with whatever else reads the same setting, so a
 * toggle writes over the keys it owns and leaves the rest of the object intact.
 */
export function mergePrivateHoldingsSettings(
  stored: unknown,
  next: PrivateHoldingsSettings,
): Record<string, unknown> {
  return { ...(isRecord(stored) ? stored : {}), ...next }
}

export function includesPrivateHoldings(
  settings: PrivateHoldingsSettings,
  measure: PrivateHoldingsMeasure,
): boolean {
  switch (measure) {
    case 'lookThrough':
    case 'spendable':
      return settings.includeInLookThrough
    case 'concentration':
      return settings.includeInConcentration
    // Liquid and the runway it feeds are about what can be spent, and an
    // unsellable holding cannot be, whatever the switch says.
    default:
      return false
  }
}

export interface ConcentrationViews<T> {
  all: T
  excludingPrivate: T
}

export function concentrationView<T>(
  views: ConcentrationViews<T>,
  settings: PrivateHoldingsSettings,
): T {
  return includesPrivateHoldings(settings, 'concentration') ? views.all : views.excludingPrivate
}

export interface ValuationParams extends Record<string, string> {
  includePrivate: string
  haircutBp: string
}

export function valuationParams(
  settings: PrivateHoldingsSettings,
  measure: PrivateHoldingsMeasure,
): ValuationParams {
  return {
    includePrivate: String(includesPrivateHoldings(settings, measure)),
    haircutBp: String(settings.haircutBp),
  }
}

/**
 * Concentration is the one figure answered twice, with the private holding and
 * without, and the comparison is the whole point of it. Both views can only be
 * built from a response that carries the holding, so it is asked for whatever
 * either switch says; `concentrationView` decides which of the two is shown.
 */
export function concentrationParams(settings: PrivateHoldingsSettings): ValuationParams {
  return { includePrivate: 'true', haircutBp: String(settings.haircutBp) }
}

export function formatBasisPoints(bp: number, fractionDigits = 1): string {
  return `${(bp / (BASIS_POINTS_FULL / 100)).toFixed(fractionDigits)}%`
}

export function formatSignedBasisPoints(bp: number, fractionDigits = 1): string {
  return `${bp > 0 ? '+' : ''}${formatBasisPoints(bp, fractionDigits)}`
}

export type Confidence = 'low' | 'medium' | 'high'

export const CONFIDENCE_NOTE: Record<Confidence, string> = {
  low: 'The round is old, small or priced off a preferred share this holding does not own.',
  medium: 'A recent round, but still a price nobody has offered for these shares.',
  high: 'A recent round with terms close to what these shares would actually fetch.',
}

/**
 * Low, medium and high are a verdict on the price; its age is not.
 *
 * Low is `caution` rather than `failed`: a holding marked off events and not off
 * a market is less certain by nature, and the tone that says "this did not work"
 * was overstating it.
 *
 * Beside the note above rather than in the two pages that read it. It was
 * written out twice, agreeing only by accident of having been copied, and the
 * `failed` this corrects survived in one of them long after the other was
 * fixed — which is what a second copy is for.
 */
export const CONFIDENCE_TONE = { low: 'caution', medium: 'caution', high: 'live' } as const

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function asHaircutBp(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isInteger(value)) return fallback
  if (value < 0 || value > BASIS_POINTS_FULL) return fallback
  return value
}
